import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const AuthModal = () => {
  const { authModal, closeAuth, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authModal) {
      setMode(authModal.mode || "login");
      setForm({ name: "", email: "", password: "" });
    }
  }, [authModal]);

  if (!authModal) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        await register(form.name, form.email, form.password);
        toast.success("Account created. Welcome to THRIFTHOUSE.");
      } else {
        await login(form.email, form.password);
        toast.success("Signed in");
      }
      const cb = authModal.onSuccess;
      closeAuth();
      if (cb) cb();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {authModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-center justify-center p-4"
          onClick={closeAuth}
          data-testid="auth-modal"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="relative glass w-full max-w-md p-8 md:p-10"
          >
            <button onClick={closeAuth} className="absolute top-5 right-5 text-neutral-400 hover:text-white" data-testid="auth-modal-close" aria-label="Close">
              <X className="w-5 h-5" />
            </button>

            <div className="w-11 h-11 border border-white/20 flex items-center justify-center mb-5">
              <Lock className="w-5 h-5" />
            </div>
            <p className="font-mono-th text-xs uppercase tracking-[0.3em] text-neutral-500">THRIFTHOUSE</p>
            <h2 className="font-display font-black text-3xl uppercase mt-2">
              {mode === "signup" ? "Create Account" : "Sign In"}
            </h2>
            {authModal.reason && (
              <p className="text-neutral-400 text-sm mt-2" data-testid="auth-reason">{authModal.reason}</p>
            )}

            <form onSubmit={submit} className="mt-7 space-y-4">
              {mode === "signup" && (
                <div>
                  <label className="text-xs text-neutral-400 mb-2 block">Full Name</label>
                  <input value={form.name} onChange={set("name")} className="w-full bg-[#13151A] border border-white/10 px-4 py-3.5 text-sm focus:outline-none focus:border-white/30" placeholder="Your name" data-testid="auth-name" />
                </div>
              )}
              <div>
                <label className="text-xs text-neutral-400 mb-2 block">Email</label>
                <input type="email" value={form.email} onChange={set("email")} className="w-full bg-[#13151A] border border-white/10 px-4 py-3.5 text-sm focus:outline-none focus:border-white/30" placeholder="you@email.com" data-testid="auth-email" />
              </div>
              <div>
                <label className="text-xs text-neutral-400 mb-2 block">Password</label>
                <input type="password" value={form.password} onChange={set("password")} className="w-full bg-[#13151A] border border-white/10 px-4 py-3.5 text-sm focus:outline-none focus:border-white/30" placeholder="••••••••" data-testid="auth-password" />
              </div>
              <button type="submit" disabled={loading} className="w-full bg-white text-black py-4 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-50 hover:bg-neutral-200 transition-colors" data-testid="auth-submit">
                {loading ? "Please wait..." : mode === "signup" ? "Create Account" : "Sign In"}
              </button>
            </form>

            <p className="text-center text-sm text-neutral-500 mt-6">
              {mode === "signup" ? "Already have an account?" : "New to THRIFTHOUSE?"}{" "}
              <button
                onClick={() => setMode(mode === "signup" ? "login" : "signup")}
                className="text-white hover:underline"
                data-testid="auth-switch-mode"
              >
                {mode === "signup" ? "Sign in" : "Create one"}
              </button>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
